import { ChangeDetectorRef, Component, ElementRef, HostListener } from '@angular/core';
import { FormBuilder, FormControl, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { debounceTime, distinctUntilChanged, merge, Subject, switchMap, takeUntil } from "rxjs";
import { map } from "rxjs/operators";

import { ContentsRepository } from "../../contents/repositories/contents.repository";
import { Checkbox } from "../../shared/components/checkmarks/checkmarks.component";
import { AbstractInFunnelComponent } from "../../shared/components/funnel/abstract.in-funnel.component";
import { SnackbarService } from "../../shared/components/snackbar/snackbar.service";
import { UploadFileService } from "../../shared/components/upload-file/upload-file.service";
import { FunnelService } from "../../shared/services/funnel.service";
import { capitalize } from "../../shared/tool-functions/capitalize";
import { EntityFormGroup } from "../../shared/types/entity-form-group";
import { SherpaRepository } from "../../users/repositories/sherpa.repository";
import { CreateDynamic, DynamicType } from "../models/dynamic.model";
import { Pillar } from "../models/pillar.model";
import {
  CreateKeyNumber,
  CreateTestimonial,
  CreateThematic,
  CreateThematicForm,
  Thematic
} from "../models/thematic.model";
import { ThematicRepository } from "../repositories/thematic.repository";

/** Pinned resource item for display */
interface PinnedResource {
  id: string;
  title: string;
}

/**
 * Pillar translation maps for display (English slug → French display name)
 * The backend stores English slugs, but we display French names to users.
 */
const PILLAR_TRANSLATIONS: Record<string, string> = {
  'individual': "L'Individu",
  'environment': "L'Environnement",
  'event': "Les Événements",
};

/** Valid English pillar slugs */
const VALID_PILLAR_SLUGS = ['individual', 'environment', 'event'];

// TODO: add "archive" button and complete warnings and missed fields notification, add complete guide to create thematic
@Component({
  selector: 'app-edit-thematic',
  templateUrl: './edit-thematic.component.html',
  styleUrls: [ './edit-thematic.component.scss' ]
})
export class EditThematicComponent extends AbstractInFunnelComponent {

  thematicForm!: EntityFormGroup<CreateThematicForm>;

  workingOnForms: FormControl<string>[] = [];

  coachingDescriptionForms: FormControl<string>[] = [];

  speechDescriptionsForms: FormControl<string>[] = [];

  testimonialForms: EntityFormGroup<CreateTestimonial>[] = [];

  keyNumberForms: EntityFormGroup<CreateKeyNumber>[] = [];

  thematic?: Thematic;

  existingThematics: Thematic[] = [];

  coachingSherpas: Checkbox[] = [];

  coachingSherpasAssociated: string[] = [];

  speechSherpas: Checkbox[] = [];

  speechSherpasAssociated: string[] = [];

  nameTaken = false;

  loading = true;

  submitted = false;

  file?: File;

  bannerFile?: File;

  pillarNames: string[] = [];

  pillars: Pillar[] = [];

  modification$ = new Subject<void>();

  errorMessages: string[] = [];

  // Pinned resources feature
  pinnedResources: PinnedResource[] = [];
  resourceSearchControl = new FormControl('');
  resourceSearchResults: PinnedResource[] = [];
  isSearchingResources = false;
  showResourceSearch = false;
  private searchSubject = new Subject<string>();

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly thematicRepository: ThematicRepository,
    private readonly sherpaRepository: SherpaRepository,
    protected override readonly route: ActivatedRoute,
    protected override readonly funnelService: FunnelService,
    private readonly snackBarService: SnackbarService,
    private readonly uploadService: UploadFileService,
    private readonly router: Router,
    private readonly contentsRepository: ContentsRepository,
    private readonly elementRef: ElementRef,
    private readonly cdr: ChangeDetectorRef,
  ) {
    super(funnelService, route);
    this.initResourceSearch();
  }

  /**
   * Close the search dropdown when clicking outside
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.showResourceSearch) return;

    const searchWrapper = this.elementRef.nativeElement.querySelector('.pinned-search-wrapper');
    if (searchWrapper && !searchWrapper.contains(event.target as Node)) {
      this.hideResourceSearch();
    }
  }

  /**
   * Initialize the resource search with debounce
   */
  private initResourceSearch(): void {
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(search => {
        if (!search || search.length < 2) {
          this.resourceSearchResults = [];
          this.isSearchingResources = false;
          return [];
        }
        this.isSearchingResources = true;
        this.cdr.detectChanges();
        return this.contentsRepository.searchByTitle(search, 10);
      }),
      takeUntil(this.destroy$)
    ).subscribe(results => {
      // Filter out already pinned resources
      const pinnedIds = new Set(this.pinnedResources.map(r => r.id));
      this.resourceSearchResults = results.filter(r => !pinnedIds.has(r.id));
      this.isSearchingResources = false;
      this.cdr.detectChanges();
    });

    // Subscribe to search input changes — also re-assert showResourceSearch
    // to guard against the document:click handler closing the dropdown
    this.resourceSearchControl.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(value => {
      if (value) {
        this.showResourceSearch = true;
      }
      this.searchSubject.next(value || '');
    });
  }

  init(): void {
    this.loading = true;
    const oldSpeechDynamic = this.thematic?.dynamics?.find(dynamic => dynamic.type === DynamicType.SPEECH);
    const oldCoachingDynamic = this.thematic?.dynamics?.find(dynamic => dynamic.type === DynamicType.COACHING);

    this.sherpaRepository.getAll().subscribe(sherpas => {
      const oldCoachingSherpas = oldCoachingDynamic?.sherpasAllowed ?? [];

      this.coachingSherpas = sherpas.map(user => {
        const key = user.fullName;
        return {
          id: user.id,
          key,
          selected: oldCoachingSherpas.map(sherpa => sherpa.id).includes(user.id)
        };
      });

      const oldSpeechSherpas = oldSpeechDynamic?.sherpasAllowed ?? [];
      this.speechSherpas = sherpas.map(user => {
        const key = user.fullName;
        return {
          id: user.id,
          key,
          selected: oldSpeechSherpas.map(sherpa => sherpa.id).includes(user.id)
        };
      });
    });

    // workingOn is optional - no required validator
    this.workingOnForms = this.thematic?.workingOn?.split('\n')?.length
      ? this.thematic.workingOn.split('\n').map(workingOn => new FormControl(workingOn, {
        nonNullable: true
      }))
      : [];


    this.testimonialForms = this.thematic?.testimonials?.length
      ? this.thematic.testimonials.map(testimonial => this.formBuilder.group({
        author: new FormControl(testimonial.author, { validators: Validators.required, nonNullable: true }),
        description: new FormControl(testimonial.description, { validators: Validators.required, nonNullable: true }),
      }))
      : [ ];

    this.keyNumberForms = this.thematic?.keyNumbers?.length
      ? this.thematic.keyNumbers.map(keyNumber => this.formBuilder.group({
        stringNumber: new FormControl(keyNumber.stringNumber, { validators: Validators.required, nonNullable: true }),
        description: new FormControl(keyNumber.description, { validators: Validators.required, nonNullable: true }),
      }))
      : [];

    // Load pinned resources if editing an existing thematic
    if (this.thematic?.pinnedContentIds?.length) {
      this.contentsRepository.getByIds(this.thematic.pinnedContentIds).subscribe(contents => {
        // Preserve the order from pinnedContentIds
        this.pinnedResources = this.thematic!.pinnedContentIds!.map(id => {
          const content = contents.find(c => c.id === id);
          return content || { id, title: 'Ressource non trouvée' };
        });
      });
    } else {
      this.pinnedResources = [];
    }

    this.speechDescriptionsForms = oldSpeechDynamic
      ? oldSpeechDynamic.description.split('\n').map(description => new FormControl(description, {
        validators: Validators.required,
        nonNullable: true
      }))
      : [ "J'ai besoin de libérer mes émotions", "Je me sens moins seul.", "Je me sens moins seul.", "Je retrouve de l'énergie. " ]
        .map(string => new FormControl(string, {
          validators: Validators.required,
          nonNullable: true
        }));


    this.coachingDescriptionForms = oldCoachingDynamic
      ? oldCoachingDynamic.description.split('\n').map(description => new FormControl(description, {
        validators: Validators.required,
        nonNullable: true
      }))
      : [ "Je suis challengé.", "J'avance et je me transforme.", "Je développe mes soft skills.", "Je suis en démarche délibérée de progrès." ]
        .map(string => new FormControl(string, {
          validators: Validators.required,
          nonNullable: true
        }));

    // Subscribe to existing forms' valueChanges to trigger validation updates
    this.subscribeToExistingForms();

    // Convert English pillar slug to French display name for the form
    const pillarDisplayName = this.thematic?.pillar?.name
      ? (PILLAR_TRANSLATIONS[this.thematic.pillar.name.toLowerCase()] || this.thematic.pillar.name)
      : '';

    this.thematicForm = this.formBuilder.group({
      slug: new FormControl(this.thematic?.slug ?? '', { validators: Validators.required, nonNullable: true }),
      name: new FormControl(this.thematic?.name ?? '', { validators: Validators.required, nonNullable: true }),
      title: new FormControl(this.thematic?.title ?? '', { validators: Validators.required, nonNullable: true }),
      resume: new FormControl(this.thematic?.resume ?? '', { validators: Validators.required, nonNullable: true }),
      pillar: new FormControl(pillarDisplayName, {
        validators: Validators.required,
        nonNullable: true
      }),
      speech: new FormControl(oldSpeechDynamic ? !oldSpeechDynamic.isArchived : false, {
        validators: Validators.required,
        nonNullable: true
      }),
      coaching: new FormControl(oldCoachingDynamic ? !oldCoachingDynamic.isArchived : false, {
        validators: Validators.required,
        nonNullable: true
      }),
    });

    this.loading = false;

    this.funnelService.validate$.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.save();
      });

    this.modification$.next();

  }

  override ngAfterViewInit() {
    super.ngAfterViewInit();

    setTimeout(() => {
      this.modification$
        .pipe(
          takeUntil(this.destroy$))
        .subscribe(() => {
          this.funnelService.canValidate$.next(true);
          this.funnelService.validateMessage$.next(!this.thematic?.isArchived ? "Enregistrer" : "Réactiver");
          if (this.submitted) {
            this.isValid();
          }
        });

      this.modification$.next();
    }, 1000);

  }

  override ngOnInit(): void {
    this.route.data
      .pipe(map(data => data.thematic))
      .subscribe((thematic) => {
        this.thematic = thematic;

        this.init();

        this.previousMessage = 'Retour à la thématique';
        super.ngOnInit();

        this.thematicRepository.getPillars().subscribe(pillars => {
          // Filter to only include valid English pillar slugs (remove duplicates like French names)
          this.pillars = pillars.filter(p => VALID_PILLAR_SLUGS.includes(p.name.toLowerCase()));
          // Display French translations to users
          this.pillarNames = this.pillars.map(pillar => PILLAR_TRANSLATIONS[pillar.name.toLowerCase()] || pillar.name);
        });


        this.thematicForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(
          value => {
            this.nameTaken = !!this.existingThematics.find(t => t.name.toLowerCase() === value.name?.toLowerCase());
            this.modification$.next();
          }
        );

        merge([
          this.thematicForm.valueChanges,
          this.keyNumberForms.map(f => f.valueChanges),
          this.workingOnForms.map(f => f.valueChanges),
          this.testimonialForms.map(f => f.valueChanges),
          this.speechDescriptionsForms.map(f => f.valueChanges),
          this.coachingDescriptionForms.map(f => f.valueChanges),
        ])
          .pipe(takeUntil(this.destroy$))
          .subscribe(() => {
            this.modification$.next();
          });

        this.loading = false;
      });
    this.thematicRepository.getAll().subscribe(thematics => {
      this.existingThematics = thematics.filter(thematic => thematic.name !== this.thematic?.name);
    });
  }

  private subscribeToExistingForms(): void {
    this.keyNumberForms.forEach(form => {
      form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.modification$.next();
      });
    });
    this.testimonialForms.forEach(form => {
      form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.modification$.next();
      });
    });
    this.workingOnForms.forEach(form => {
      form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.modification$.next();
      });
    });
  }

  addWorkingOnForm(): void {
    // workingOn is optional - no required validator
    const newForm = new FormControl('', { nonNullable: true });
    newForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.modification$.next();
    });
    this.workingOnForms.push(newForm);
    this.modification$.next();
  }

  removeWorkingOnForm(index: number): void {
    this.workingOnForms.splice(index, 1);
    this.modification$.next();
  }

  addCoachingDescriptionForm(): void {
    this.coachingDescriptionForms.push(new FormControl('', { validators: Validators.required, nonNullable: true }));
    this.modification$.next();
  }

  removeCoachingDescriptionForm(index: number): void {
    this.coachingDescriptionForms.splice(index, 1);
    this.modification$.next();
  }

  addSpeechDescriptionForm(): void {
    this.speechDescriptionsForms.push(new FormControl('', { validators: Validators.required, nonNullable: true }));
    this.modification$.next();
  }

  removeSpeechDescriptionForm(index: number): void {
    this.speechDescriptionsForms.splice(index, 1);
    this.modification$.next();
  }

  addTestimonialForm(): void {
    const newForm = this.formBuilder.group({
      author: new FormControl('', { validators: Validators.required, nonNullable: true }),
      description: new FormControl('', { validators: Validators.required, nonNullable: true }),
    });
    newForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.modification$.next();
    });
    this.testimonialForms.push(newForm);
    this.modification$.next();
  }

  removeTestimonialForm(index: number): void {
    this.testimonialForms.splice(index, 1);
    this.modification$.next();
  }

  addKeyNumberForm(): void {
    const newForm = this.formBuilder.group({
      stringNumber: new FormControl('', { validators: Validators.required, nonNullable: true }),
      description: new FormControl('', { validators: Validators.required, nonNullable: true }),
    });
    newForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.modification$.next();
    });
    this.keyNumberForms.push(newForm);
    this.modification$.next();
  }

  removeKeyNumberForm(index: number): void {
    this.keyNumberForms.splice(index, 1);
    this.modification$.next();
  }

  get gotCoaching(): boolean {
    return this.thematicForm.controls.coaching.value;
  }

  get gotSpeech(): boolean {
    return this.thematicForm.controls.speech.value;
  }

  nameWorks(): boolean {
    return !this.nameTaken && !!this.thematicForm.controls.name.value;
  }

  formValid(): boolean {
    return this.thematicForm.valid
      && this.nameWorks()
      && this.testimonialForms.every(form => form.valid)
      && this.keyNumberForms.every(form => form.valid);
  }

  getDynamics(): CreateDynamic[] {
    const result: CreateDynamic[] = [];
    if (this.thematicForm.controls.speech.value) {
      result.push({
        descriptions: this.speechDescriptionsForms.map(form => form.value),
        type: DynamicType.SPEECH,
        sherpasAllowed: this.speechSherpasAssociated
      });
    }
    if (this.thematicForm.controls.coaching.value) {
      result.push({
        descriptions: this.coachingDescriptionForms.map(form => form.value),
        type: DynamicType.COACHING,
        sherpasAllowed: this.coachingSherpasAssociated
      });
    }

    return result;
  }

  isValid(): boolean {
    this.errorMessages = [];

    if (this.nameTaken) {
      this.errorMessages.push('Le nom de thématique est déjà pris');
    }
    if (!this.thematicForm.controls.slug.valid) {
      this.errorMessages.push('Label manquant');
    }
    if (!this.thematicForm.controls.pillar.valid) {
      this.errorMessages.push('Pilier manquant');
    }
    if (!this.thematicForm.controls.name.valid) {
      this.errorMessages.push('Nom court manquant');
    }
    if (!this.thematicForm.controls.title.valid) {
      this.errorMessages.push('Intitulé complet du parcours manquant');
    }
    if (!this.thematicForm.controls.resume.valid) {
      this.errorMessages.push('Résumé manquant');
    }

    if(this.getDynamics().length === 0) {
      this.errorMessages.push("Cette thématique n'a pas de dynamique");
    }


    this.getDynamics().forEach(dynamic => {
      if (dynamic.type === DynamicType.COACHING) {
        if (this.gotCoaching && dynamic.sherpasAllowed.length === 0) {
          this.errorMessages.push("La dynamique coaching en groupe n'a pas de sherpa");
        }
      }
      if (dynamic.type === DynamicType.SPEECH) {
        if (this.gotSpeech && dynamic.sherpasAllowed.length === 0) {
          this.errorMessages.push("La dynamique parole en groupe n'a pas de sherpa");
        }
      }
    });

    if (this.keyNumberForms.some(form => !form.valid)) {
      this.errorMessages.push("Au moins un chiffre clé n'est pas rempli correctement");
    }
    if (this.testimonialForms.some(form => !form.valid)) {
      this.errorMessages.push("Au moins un témoignage n'est pas rempli correctement");
    }
    // workingOn is optional - no validation needed

    if (!this.thematic?.id) {
      if (!this.file) {
        this.errorMessages.push('Image manquante');
      }
    }

    return !this.errorMessages.length;

    // return (!!this.file || !!this.thematic?.id)
    //   && this.getDynamics()
    //     .filter(dynamic =>
    //       !!dynamic.descriptions.length
    //       && !!dynamic.sherpasAllowed.length)
    //     .length === +this.gotCoaching + +this.gotSpeech
    //   && this.formValid();
  }

  title(): string {
    return this.thematic ? "Modification d'une thématique" : "Création d'une thématique";
  }

  updateCoachingSherpasAssociated(checkboxes: Checkbox[]) {
    this.coachingSherpasAssociated = checkboxes.filter(checkbox => checkbox.selected).map(checkbox => checkbox.id);
    this.modification$.next();
  }

  updateSpeechSherpasAssociated(checkboxes: Checkbox[]) {
    this.speechSherpasAssociated = checkboxes.filter(checkbox => checkbox.selected).map(checkbox => checkbox.id);
    this.modification$.next();
  }

  updateFile(file: File): void {
    this.file = file;
    this.modification$.next();
  }

  updateBannerFile(file: File): void {
    this.bannerFile = file;
    this.modification$.next();
  }

  // Pinned resources methods

  /**
   * Show the resource search field (triggered on focus)
   */
  showAddResource(): void {
    this.showResourceSearch = true;
  }

  /**
   * Hide the resource search field
   */
  hideResourceSearch(): void {
    this.showResourceSearch = false;
    this.resourceSearchControl.setValue('');
    this.resourceSearchResults = [];
  }

  /**
   * Clear the search input and results (but keep search open)
   */
  clearResourceSearch(): void {
    this.resourceSearchControl.setValue('');
    this.resourceSearchResults = [];
  }

  /**
   * Add a resource to the pinned list
   */
  addPinnedResource(resource: PinnedResource): void {
    if (this.pinnedResources.length >= 3) {
      return;
    }
    this.pinnedResources.push(resource);

    // Remove added resource from search results
    this.resourceSearchResults = this.resourceSearchResults.filter(r => r.id !== resource.id);

    // If max reached, close the search; otherwise clear input for next search
    if (this.pinnedResources.length >= 3) {
      this.hideResourceSearch();
    } else {
      this.resourceSearchControl.setValue('');
    }

    this.modification$.next();
  }

  /**
   * Remove a resource from the pinned list
   */
  removePinnedResource(index: number): void {
    this.pinnedResources.splice(index, 1);
    this.modification$.next();
  }

  /**
   * Check if we can add more pinned resources (max 3)
   */
  canAddMoreResources(): boolean {
    return this.pinnedResources.length < 3;
  }

  save(): void {
    this.submitted = true;

    if (!this.isValid()) {
      return;
    }

    this.funnelService.componentLoading$.next(true);

    const speechDynamic: CreateDynamic | undefined = this.gotSpeech ? {
      type: DynamicType.SPEECH,
      descriptions: this.speechDescriptionsForms.map(descriptionForm => descriptionForm.value),
      sherpasAllowed: this.speechSherpasAssociated
    } : undefined;
    const coachingDynamic: CreateDynamic | undefined = this.gotCoaching ? {
      type: DynamicType.COACHING,
      descriptions: this.coachingDescriptionForms.map(descriptionForm => descriptionForm.value),
      sherpasAllowed: this.coachingSherpasAssociated
    } : undefined;

    // Find pillar by matching French display name to English slug
    const selectedFrenchName = this.thematicForm.controls.pillar.value;
    const selectedPillar = this.pillars.find(p =>
      PILLAR_TRANSLATIONS[p.name.toLowerCase()] === selectedFrenchName
    );

    const result = {
      ...this.thematicForm.value,
      pillar: selectedPillar?.id,
      testimonials: this.testimonialForms.map(testimonialForm => testimonialForm.value) as CreateTestimonial[],
      keyNumbers: this.keyNumberForms.map(keyNumberForm => keyNumberForm.value) as CreateKeyNumber[],
      // Filter out empty workingOn values since it's optional
      workingOn: this.workingOnForms.map(workingOnForm => workingOnForm.value).filter(v => v?.trim()),
      dynamics: [ speechDynamic, coachingDynamic ].filter((dynamic): dynamic is CreateDynamic => !!dynamic),
      // Include pinned resource IDs (max 3)
      pinnedContentIds: this.pinnedResources.map(r => r.id),
    } as CreateThematic;


    this.loading = true;
    if (this.thematic) {
      this.thematicRepository.patch(this.thematic.id, result)
        .subscribe(thematic => {
          const isReactivation = this.thematic?.isArchived;

          this.thematic = thematic;
          this.init();

          if (this.file) {
            this.uploadService.uploadFile(this.file, "thematics", thematic.id).subscribe({
              next: () => {
                this.snackBarService.pushMessage(
                  isReactivation ? 'Thématique activée' : 'Modification enregistrée',
                  'success'
                );
                this.funnelService.componentLoading$.next(false);
              },
              error: () => {
                this.snackBarService.pushMessage(
                  isReactivation ? 'Thématique activée' : 'Modification enregistrée',
                  'success'
                );
                this.funnelService.componentLoading$.next(false);
              }
            });
          } else {
            this.snackBarService.pushMessage(
              isReactivation ? 'Thématique activée' : 'Modification enregistrée',
              'success'
            );
            this.funnelService.componentLoading$.next(false);
          }

          if(this.bannerFile) {
            this.uploadService.uploadFile(this.bannerFile, "thematics", thematic.id, true).subscribe({
              next: () => {
                this.funnelService.componentLoading$.next(false);
              },
              error: () => {
                this.funnelService.componentLoading$.next(false);
              }
            });
          }

          this.loading = false;
        });
    }

    if (!this.thematic && this.file) {
      this.thematicRepository.create(result)
        .subscribe(thematic => {
          if (!thematic?.id) {
            this.snackBarService.pushMessage('Erreur lors de la création', 'error');
            this.funnelService.componentLoading$.next(false);
            this.loading = false;
            return;
          }

          this.thematic = thematic;
          this.init();

          if (this.file) {
            this.uploadService.uploadFile(this.file, "thematics", thematic.id).subscribe({
              next: () => {
                this.snackBarService.pushMessage(
                  'Thématique créée',
                  'success'
                );
                this.funnelService.componentLoading$.next(false);
              },
              error: () => {
                this.snackBarService.pushMessage(
                  'Thématique créée',
                  'success'
                );
                this.funnelService.componentLoading$.next(false);
              }
            });
          } else {
            this.loading = false;
            this.funnelService.componentLoading$.next(false);
          }

          if(this.bannerFile) {
            this.uploadService.uploadFile(this.bannerFile, "thematics", thematic.id, true).subscribe({
              next: () => {
                this.funnelService.componentLoading$.next(false);
              },
              error: () => {
                this.funnelService.componentLoading$.next(false);
              }
            });
          }
        });
    }
  }

  archiveThematic() {
    if (this.thematic?.id) {

      this.thematicRepository.deleteById(this.thematic.id)
        .subscribe(() => {
          this.snackBarService.pushMessage('Thématique archivée', 'success');
        });
    }
  }
}
